import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  LogOut,
  Calendar,
  Clock,
  MapPin,
  Users,
  CheckCircle,
  AlertCircle,
  Loader2,
  UserCircle,
  Trophy,
  ListOrdered,
  XCircle,
  RefreshCw
} from "lucide-react";

export default function PlayerDashboard({ player, onLogout }) {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegistering, setIsRegistering] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/player-dashboard/${player.email}`);
      setDashboardData(response.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setIsLoading(false);
    }
  }, [player?.email]);

  useEffect(() => {
    if (player?.email) {
      fetchDashboard();
    }
  }, [player?.email, fetchDashboard]);

  const handleRegister = async (gameId) => {
    setIsRegistering(gameId);
    try {
      const response = await axios.post(`${API}/game-registrations/${gameId}/register`, {
        player_email: player.email,
        player_name: player.name
      });
      toast.success(response.data.message);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao se inscrever");
    } finally {
      setIsRegistering(null);
    }
  };

  const handleCancel = async (gameId) => {
    if (!confirm("Tem certeza que deseja cancelar sua inscrição?")) return;
    
    setIsRegistering(gameId);
    try {
      const response = await axios.post(`${API}/game-registrations/${gameId}/cancel`, {
        player_email: player.email
      });
      toast.success(response.data.message);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cancelar");
    } finally {
      setIsRegistering(null);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="VolleyxBrasil" className="w-12 h-12 md:w-14 md:h-14" />
              <div>
                <p className="text-sm text-muted-foreground">
                  Área do Jogador
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right hidden md:block">
                <p className="font-medium">{dashboardData?.name || player.name}</p>
                <p className="text-sm text-muted-foreground">{player.email}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={fetchDashboard}
                title="Atualizar"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onLogout}
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-2 md:px-4 py-6 md:py-8 space-y-6">
        {/* Welcome Card */}
        <Card className="bg-gradient-to-r from-primary/20 to-primary/5 border-primary/30">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                <UserCircle className="h-10 w-10 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold">Olá, {dashboardData?.name || player.name}!</h2>
                <p className="text-muted-foreground">Bem-vindo ao seu painel de jogador</p>
              </div>
              <div className="text-right hidden md:block">
                <div className="text-3xl font-bold text-primary">{dashboardData?.stats?.total_registrations || 0}</div>
                <p className="text-sm text-muted-foreground">Inscrições</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-6 md:gap-8 lg:grid-cols-2">
          {/* Open Games for Registration */}
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-green-500" />
                Jogos Abertos para Inscrição
              </CardTitle>
              <CardDescription>
                Inscreva-se nos próximos jogos
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!dashboardData?.open_games?.length ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum jogo aberto no momento</p>
                  <p className="text-sm">Aguarde o administrador abrir as inscrições</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {dashboardData.open_games.map((game) => (
                    <div 
                      key={game.id}
                      className="p-4 rounded-lg bg-secondary/30 border border-border/50"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold">{game.title}</h3>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(game.date).toLocaleDateString('pt-BR')}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {game.time}
                            </span>
                          </div>
                          {game.location && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              {game.location}
                            </p>
                          )}
                        </div>
                        <Badge variant={game.registered_count >= game.max_players ? "destructive" : "default"}>
                          {game.registered_count}/{game.max_players}
                        </Badge>
                      </div>
                      
                      {/* Progress bar */}
                      <div className="w-full bg-secondary rounded-full h-2 mb-3">
                        <div 
                          className={`h-2 rounded-full transition-all ${game.registered_count >= game.max_players ? 'bg-red-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min((game.registered_count / game.max_players) * 100, 100)}%` }}
                        />
                      </div>

                      {game.is_registered ? (
                        <div className="flex items-center justify-between">
                          <Badge variant="default" className="bg-green-500/20 text-green-400">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Inscrito
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => handleCancel(game.id)}
                            disabled={isRegistering === game.id}
                          >
                            {isRegistering === game.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4 mr-1" />}
                            Cancelar
                          </Button>
                        </div>
                      ) : game.is_waitlisted ? (
                        <div className="flex items-center justify-between">
                          <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-400">
                            <ListOrdered className="h-3 w-3 mr-1" />
                            Lista de Espera
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => handleCancel(game.id)}
                            disabled={isRegistering === game.id}
                          >
                            {isRegistering === game.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4 mr-1" />}
                            Sair da Fila
                          </Button>
                        </div>
                      ) : (
                        <Button
                          className="w-full"
                          onClick={() => handleRegister(game.id)}
                          disabled={isRegistering === game.id}
                        >
                          {isRegistering === game.id ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <Users className="h-4 w-4 mr-2" />
                          )}
                          {game.registered_count >= game.max_players 
                            ? `Entrar na Lista de Espera (${game.waitlist_count} na fila)`
                            : "Inscrever-se"
                          }
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* My Games */}
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Minhas Inscrições
              </CardTitle>
              <CardDescription>
                Jogos que você está inscrito
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!dashboardData?.my_games?.length ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Trophy className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Você ainda não está inscrito em nenhum jogo</p>
                  <p className="text-sm">Inscreva-se em um jogo aberto</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {dashboardData.my_games.map((game) => (
                    <div 
                      key={game.id}
                      className={`p-4 rounded-lg border ${
                        game.status === 'registered' 
                          ? 'bg-green-500/5 border-green-500/30' 
                          : 'bg-yellow-500/5 border-yellow-500/30'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold">{game.title}</h3>
                            {game.status === 'registered' ? (
                              <Badge variant="default" className="bg-green-500/20 text-green-400 text-xs">
                                #{game.position}
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-400 text-xs">
                                Espera #{game.waitlist_position}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(game.date).toLocaleDateString('pt-BR')}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {game.time}
                            </span>
                          </div>
                          {game.location && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              {game.location}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <Badge variant={game.is_open ? "default" : "secondary"}>
                            {game.is_open ? "Aberto" : "Fechado"}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {game.total_registered}/{game.max_players} jogadores
                          </p>
                        </div>
                      </div>
                      
                      {game.is_open && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-2 text-destructive w-full"
                          onClick={() => handleCancel(game.id)}
                          disabled={isRegistering === game.id}
                        >
                          {isRegistering === game.id ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <XCircle className="h-4 w-4 mr-1" />}
                          Cancelar Inscrição
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
